import { CELL } from '../core/config';
import { industrialBuilding } from './industrial-buildings';

export type Material = 'brick' | 'concrete' | 'wood' | 'metal' | 'glass' | 'foliage';
export const MATERIALS = {
  brick: { strength: 1.4, density: 1.3, color: 0xd29679, fuel: 0, ignition: 100 },
  concrete: { strength: 2.6, density: 2.1, color: 0xd9d8cb, fuel: 0, ignition: 100 },
  wood: { strength: 0.85, density: 0.65, color: 0xae805b, fuel: 1, ignition: 0.55 },
  metal: { strength: 4.2, density: 2.7, color: 0xa3b7b8, fuel: 0, ignition: 100 },
  glass: { strength: 0.38, density: 0.55, color: 0x68a6ad, fuel: 0, ignition: 100 },
  foliage: { strength: 0.42, density: 0.12, color: 0x789766, fuel: 0.85, ignition: 0.32 },
} as const;
export type CellRole = 'column' | 'beam' | 'slab' | 'roof' | 'wall' | 'facade' | 'glass' | 'prop';
export interface CellBounds {
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
}
export interface OccupiedPart extends CellBounds {
  color?: number;
}
export interface Cell {
  id: number;
  x: number;
  y: number;
  z: number;
  hp: number;
  material: Material;
  color: number;
  role?: CellRole;
  capacity?: number;
  heat?: number;
  fracture?: number;
  root?: boolean;
  /** Optional occupied shape inside a cell, in cell-relative units; topology still uses the cell ID. */
  bounds?: CellBounds;
  /** Shared item identity, exact occupied pieces; empty space stays empty. */
  parts?: OccupiedPart[];
  /** Lightweight trim belongs to this real damageable window pane. */
  windowTrim?: number;
}
export type Archetype =
  | 'glass-office'
  | 'setback-skyscraper'
  | 'masonry-highrise'
  | 'modern-apartment'
  | 'courtyard'
  | 'rowhouse'
  | 'pitched-house'
  | 'semi-detached'
  | 'corner-shop'
  | 'warehouse'
  | 'civic'
  | 'barn'
  | 'point-tower'
  | 'l-office'
  | 't-office'
  | 'twin-tower'
  | 'terraced-office'
  | 'slab-apartment'
  | 'stepped-apartment'
  | 'u-school'
  | 'sawtooth-factory'
  | 'hangar'
  | 'market-hall'
  | 'mall'
  | 'round-civic'
  | 'chapel'
  | 'grain-silo';
export type PropKind =
  | 'tree'
  | 'bench'
  | 'lamp'
  | 'fence'
  | 'planter'
  | 'shelter'
  | 'fountain'
  | 'paving'
  | 'bridge'
  | 'road'
  | 'aircraft'
  | 'furniture';
export type BuildingKind =
  'shop' | 'apartment' | 'tower' | 'arcade' | 'overhang' | 'terrace' | Archetype | PropKind;
export interface Building {
  exterior?: {
    addressCell: number;
    signature: string;
    palette: number;
    bays: number;
    rhythm: number;
    trim: number;
    window: number;
    roof: number;
    roofColor?: number;
    composition?: number;
  };
  landmark?: { baselineHeight: number; multiplier: number; design: string; occupiedFloors: number[] };
  service?: Facility['kind'];
  id: number;
  name: string;
  kind: BuildingKind;
  x: number;
  z: number;
  w: number;
  d: number;
  h: number;
  color: number;
  accent: number;
  cells: Cell[];
  category?: 'building' | 'prop' | 'bridge';
  district?: string;
  chunk?: string;
  baseY?: number;
  /** Constructed detail whose occupied parts directly define its visible surface. */
  exactGeometry?: boolean;
  roofLevel?: number;
  roofSpan?: number;
  rotation?: { x: number; y: number; z: number; w: number };
  roadEdge?: number;
  parentCell?: number;
  furniture?:
    | 'desk'
    | 'chair'
    | 'bench'
    | 'bed'
    | 'shelf'
    | 'counter'
    | 'workbench'
    | 'crate'
    | 'sofa'
    | 'partition'
    | 'cabinet'
    | 'kitchen';
}
export interface RoadNode {
  id: number;
  x: number;
  z: number;
  y?: number;
  layer?: string;
}
export interface RoadEdge {
  restricted?: boolean;
  id: number;
  from: number;
  to: number;
  width: number;
  kind: string;
  bridgeId?: number;
  profile?: { x: number; y: number; z: number }[];
  surfaceIds?: number[];
  layer?: string;
  pavementWidth?: number;
  clearance?: number;
  maxGrade?: number;
  /** A composed bank approach uses the full polyline for road and foot travel. */
  curved?: boolean;
  maxVehicleLength?: number;
  bridgeZ?: number;
}
export interface District {
  id: string;
  name: string;
  x: number;
  z: number;
  radius: number;
}
export interface City {
  /** Explicit user-selected simpler street life; service crews keep their tasks. */
  streetLife?: boolean;
  strategic?: import('./strategic-districts').StrategicDistricts;
  coast?: import('./coast').CoastLayout;
  marina?: import('./marina').MarinaLayout;
  habitats?: import('./habitats').Habitats;
  landscape?: import('./landscape').Landscape;
  terrain?: import('./terrain').Terrain;
  logistics?: import('./logistics').LogisticsLayout;
  streetTransit?: import('./street-transit').StreetTransitLayout;
  helipads?: import('./helipads').Helipad[];
  airport?: import('./airport').AirportLayout;
  rail?: import('./rail').RailLayout;
  regional?: boolean;
  logicalPopulation?: number;
  ordinaryTraffic?: number;
  facilities?: Facility[];
  seed: number;
  buildings: Building[];
  roads: number[];
  half: number;
  props?: Building[];
  bridges?: Building[];
  roadGraph?: { nodes: RoadNode[]; edges: RoadEdge[] };
  districts?: District[];
  river?: { points: { x: number; z: number }[]; width: number };
  chunkSize?: number;
  initialTarget?: { x: number; z: number };
  initialView?: { targetY: number; distance: number };
  infrastructure?: Building[];
  interiors?: Interior[];
  places?: Place[];
}
export interface Facility {
  duties?: Place[];
  id: string;
  kind: 'fire' | 'police' | 'hospital' | 'depot';
  buildingId: number;
  edge: number;
  node: number;
  entry: { x: number; y: number; z: number };
  bays: { x: number; y: number; z: number }[];
  beds: {
    x: number;
    y: number;
    z: number;
    supportCell: number;
    furnitureId?: number;
    pose?: Place['pose'];
  }[];
}
export type PlaceKind =
  | 'watch'
  | 'exercise'
  | 'gate'
  | 'dock'
  | 'home'
  | 'desk'
  | 'display'
  | 'counter'
  | 'kitchen'
  | 'staff'
  | 'seat'
  | 'materials'
  | 'workbench'
  | 'maintenance'
  | 'loading'
  | 'queue'
  | 'stop';
export interface Place {
  access?: 'military' | 'staff';
  id: string;
  kind: PlaceKind;
  name: string;
  buildingId?: number;
  floor: number;
  x: number;
  y: number;
  z: number;
  supportCell?: number;
  furnitureId?: number;
  capacity: number;
  duration: number;
  facing?: number;
  serves?: string;
  tenant?: string;
  pose?: {
    x: number;
    y: number;
    z: number;
    yaw: number;
    posture: 'seated' | 'lying';
    seatHeight: number;
    duration: number;
  };
}
export interface Interior {
  buildingId: number;
  use: 'office' | 'apartment' | 'shop' | 'workshop';
  floors: number[];
  floorHeight: number;
  doorX: number;
  entrance: { x: number; y: number; z: number };
  liftX: number;
  liftZ: number;
  rooms?: {
    id: string;
    floor: number;
    name: string;
    x: number;
    z: number;
    w: number;
    d: number;
    places: string[];
    fixtures: number[];
  }[];
}
export function seeded(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const key = (x: number, y: number, z: number): number => x + 32 * z + 1024 * y;
export function createBuilding(
  id: number,
  x: number,
  z: number,
  kind: BuildingKind,
  w = 6,
  d = 6,
  floors = 3,
  color = 0xe2dfcf,
  accent = 0x467f7a,
): Building {
  const h = floors * 3 + 1;
  const b: Building = { id, name: `${kind} ${id + 1}`, x, z, kind, w, d, h, color, accent, cells: [] };
  for (let y = 0; y < h; y++)
    for (let iz = 0; iz < d; iz++)
      for (let ix = 0; ix < w; ix++) {
        const inset = kind === 'tower' && y > h - 7 ? 1 : 0;
        if (ix < inset || ix >= w - inset || iz < inset || iz >= d - inset) continue;
        const edge = ix === inset || ix === w - inset - 1 || iz === inset || iz === d - inset - 1;
        const slab = y % 3 === 0;
        const column = (ix === 0 || ix === w - 1) && (iz === 0 || iz === d - 1 || iz === Math.floor(d / 2));
        let occupied = edge || slab;
        if ((kind === 'arcade' || kind === 'overhang') && y < 3) occupied = column;
        if (kind === 'overhang' && y < 3 && ix >= w - 2) occupied = false;
        if (!occupied) continue;
        const warmRoof = y === h - 1 && (kind === 'terrace' || kind === 'shop');
        const material: Material =
          kind === 'overhang' && y === 3
            ? 'metal'
            : warmRoof
              ? 'wood'
              : slab || column
                ? 'concrete'
                : 'brick';
        const c = warmRoof ? 0xba7d60 : y === h - 1 ? 0xb5bcb7 : slab ? 0xe7e5d7 : color;
        b.cells.push({
          id: id * 1000000 + key(ix, y, iz),
          x: ix,
          y,
          z: iz,
          hp: MATERIALS[material].strength,
          material,
          color: c,
          role: kind === 'overhang' && y === 3 ? 'beam' : column ? 'column' : slab ? 'slab' : 'wall',
        });
      }
  return b;
}

/** Nine deliberately arranged blocks: eight built lots and one central garden. */
export function generateCity(seed = 73421): City {
  const random = seeded(seed),
    buildings: Building[] = [];
  const colors = [0xe1d8c2, 0xbcd0c7, 0xd9977c, 0xdfc67b, 0xe1e4dc, 0x89b0ad];
  const accents = [0x397f79, 0xb6654d, 0x486879, 0xc39d4b];
  const add = (x: number, z: number, kind: BuildingKind, w: number, d: number, floors: number) => {
    const id = buildings.length;
    buildings.push(
      createBuilding(
        id,
        x,
        z,
        kind,
        w,
        d,
        floors,
        colors[Math.floor(random() * colors.length)],
        accents[id % accents.length],
      ),
    );
  };
  for (let bz = -1; bz <= 1; bz++)
    for (let bx = -1; bx <= 1; bx++) {
      if (bx === 0 && bz === 0) continue;
      const x = bx * 30 - 11,
        z = bz * 30 - 11;
      if (bz === 1 && bx === 0) {
        add(x, z, 'shop', 5, 6, 1);
        add(x + 6.5, z, 'shop', 5, 6, 2);
        add(x + 13, z, 'shop', 5, 6, 1);
        add(x, z + 12, 'apartment', 15, 6, 3);
      } else if (bx === -1 && bz === 0) {
        add(x, z, 'arcade', 8, 7, 3);
        add(x + 12, z, 'tower', 6, 6, 7);
        add(x, z + 13, 'terrace', 8, 6, 2);
        add(x + 13, z + 13, 'shop', 5, 6, 1);
      } else if (bx === 1 && bz === 0) {
        add(x, z, 'overhang', 9, 7, 4);
        add(x + 14, z, 'shop', 5, 7, 2);
        add(x, z + 13, 'apartment', 9, 6, 3);
        add(x + 14, z + 13, 'terrace', 5, 6, 2);
      } else {
        add(x, z, bx === bz ? 'tower' : 'apartment', 7, 7, 3 + Math.floor(random() * 3));
        add(x + 13, z, 'terrace', 6, 7, 2 + Math.floor(random() * 2));
        add(x, z + 13, 'apartment', 8, 6, 2 + Math.floor(random() * 3));
        if (!(bx === -1 && bz === -1) && !(bx === 1 && bz === -1)) add(x + 14, z + 13, 'shop', 5, 6, 2);
      }
    }
  return { seed, buildings, roads: [-45, -15, 15, 45], half: 54 };
}
export const cellCenter = (c: Cell) => ({
  x: (c.x + 0.5) * CELL,
  y: (c.y + 0.5) * CELL,
  z: (c.z + 0.5) * CELL,
});

/** The cell grid is shared by structure, collision, rendering and targeting. */
export function createArchetype(
  id: number,
  x: number,
  z: number,
  kind: Archetype,
  w = 9,
  d = 8,
  floors = 5,
  color = 0xdfded3,
  accent = 0x578f98,
): Building {
  const pitched = [
    'pitched-house',
    'semi-detached',
    'barn',
    'rowhouse',
    'chapel',
    'market-hall',
    'sawtooth-factory',
  ].includes(kind);
  const floorHeight = ['warehouse', 'civic', 'hangar', 'market-hall', 'sawtooth-factory'].includes(kind)
    ? 4
    : 3;
  const roofY = kind === 'chapel' ? 6 : floors * floorHeight;
  const roofSpan =
    kind === 'rowhouse' || kind === 'sawtooth-factory' ? 5 : kind === 'semi-detached' ? Math.ceil(w / 2) : w;
  const ridge = pitched ? Math.ceil(roofSpan * 0.3 + 0.2) : 0;
  const b: Building = {
    id,
    x,
    z,
    kind,
    w,
    d,
    h: roofY + ridge + 1,
    color,
    accent,
    category: 'building',
    roofLevel: roofY,
    roofSpan,
    cells: [],
    name: `${kind.replaceAll('-', ' ')} ${id + 1}`,
  };
  if (kind === 'grain-silo' || kind === 'hangar') return industrialBuilding(b, floors);
  const filled = new Map<number, Cell>();
  const put = (ix: number, y: number, iz: number, material: Material, role: CellRole, tint: number) => {
    const k = key(ix, y, iz);
    filled.set(k, {
      id: id * 1000000 + k,
      x: ix,
      y,
      z: iz,
      hp: MATERIALS[material].strength,
      material,
      role,
      color: tint,
    });
  };
  const insetAt = (y: number) =>
    kind === 'setback-skyscraper'
      ? y > roofY - 9
        ? 2
        : y > roofY - 18
          ? 1
          : 0
      : kind === 'point-tower' && y > 9
        ? 1
        : 0;
  const footprint = (ix: number, y: number, iz: number) => {
    const inset = insetAt(y);
    if (ix < inset || ix >= w - inset || iz < inset || iz >= d - inset) return false;
    if (kind === 'courtyard' && ix >= 3 && ix < w - 3 && iz >= 3 && iz < d - 3) return false;
    if (kind === 'modern-apartment' && ix >= w - 3 && iz < Math.floor(d / 2) && y > 3) return false;
    if (kind === 'corner-shop' && ix >= w - 2 && iz >= d - 2) return false;
    if (kind === 'l-office' && ix >= 5 && iz >= 5) return false;
    if (kind === 't-office' && iz >= 5 && Math.abs(ix - (w - 1) / 2) > 2.5) return false;
    if (kind === 'twin-tower' && y > 6 && ix >= 5 && ix < w - 5) return false;
    if (kind === 'terraced-office' && ix < Math.floor(y / 6)) return false;
    if (kind === 'stepped-apartment' && y > roofY - Math.floor(ix / 4) * 3) return false;
    if (kind === 'u-school' && ix >= 4 && ix < w - 4 && iz >= 4) return false;
    if (kind === 'mall' && y > 0 && ix >= 5 && ix < w - 5 && iz >= 5 && iz < d - 5) return false;
    if (
      kind === 'round-civic' &&
      ((ix - (w - 1) / 2) / (w / 2)) ** 2 + ((iz - (d - 1) / 2) / (d / 2)) ** 2 > 1
    )
      return false;
    return true;
  };
  for (let y = 0; y <= roofY; y++)
    for (let iz = 0; iz < d; iz++)
      for (let ix = 0; ix < w; ix++) {
        if (!footprint(ix, y, iz)) continue;
        const edge =
          !footprint(ix - 1, y, iz) ||
          !footprint(ix + 1, y, iz) ||
          !footprint(ix, y, iz - 1) ||
          !footprint(ix, y, iz + 1);
        const column =
          ((ix % 3 === 0 || ix === w - 1) && (iz % 3 === 0 || iz === d - 1)) ||
          (floors > 6 && Math.abs(ix - w / 2) < 1.1 && Math.abs(iz - d / 2) < 1.1);
        const beam = y % floorHeight === floorHeight - 1 && (ix % 3 === 0 || iz % 3 === 0) && !edge;
        const slab = y % floorHeight === 0;
        if (!edge && !slab && !column && !beam) continue;
        let material: Material = 'concrete';
        let role: CellRole = column
          ? 'column'
          : slab
            ? y === roofY
              ? 'roof'
              : 'slab'
            : beam
              ? 'beam'
              : 'facade';
        let tint = slab ? 0xd0d3cb : 0xe4e1d5;
        if (column || beam) {
          material = kind === 'glass-office' || kind === 'warehouse' ? 'metal' : 'concrete';
          tint = material === 'metal' ? 0x93a8a8 : 0xd5d3c8;
        }
        if (edge && !slab && !column) {
          const glass =
            kind === 'glass-office' ||
            ['l-office', 't-office', 'twin-tower', 'terraced-office', 'mall'].includes(kind) ||
            kind === 'point-tower' ||
            (kind === 'setback-skyscraper' && (ix + iz) % 3 !== 0) ||
            (kind === 'civic' && iz === 0) ||
            (kind === 'corner-shop' && y < 3 && (ix + iz) % 3 !== 0);
          material = glass ? 'glass' : kind === 'barn' ? 'wood' : kind === 'warehouse' ? 'metal' : 'brick';
          role = glass ? 'glass' : kind === 'masonry-highrise' || kind === 'rowhouse' ? 'wall' : 'facade';
          tint = glass ? accent : color;
          if (kind === 'warehouse' && iz === 0 && ix > 1 && ix < w - 2 && y < 3) tint = accent;
        }
        if (kind === 'barn' && (column || beam)) {
          material = 'wood';
          tint = 0x917250;
        }
        // Paired entrance columns, unlike a solid wall, reveal the structural podium.
        if (kind === 'civic' && iz === 0 && y > 0 && y < 4 && ix % 3 !== 0) continue;
        if (kind === 'glass-office' && y < 3 && edge && !column && iz === 0) continue;
        put(ix, y, iz, material, role, tint);
      }
  if (pitched)
    for (let iz = 0; iz < d; iz++)
      for (let ix = 0; ix < w; ix++) {
        const roofX = ix % roofSpan;
        const top = Math.ceil(
          Math.max(Math.min(roofX, roofSpan - roofX), Math.min(roofX + 1, roofSpan - roofX - 1)) * 0.6 + 0.2,
        );
        for (let ry = 1; ry <= top; ry++) {
          const gable = iz === 0 || iz === d - 1;
          put(
            ix,
            roofY + ry,
            iz,
            'wood',
            'roof',
            gable && ry < top ? color : kind === 'barn' ? 0x687b75 : 0xa76f53,
          );
        }
      }
  // Roof plant is occupied metal, so it can be hit and leaves with the roof.
  if (pitched && kind !== 'barn' && w >= 8) {
    const ix = 2,
      iz = d - 3;
    const yy = Math.max(...[...filled.values()].filter((c) => c.x === ix && c.z === iz).map((c) => c.y));
    put(ix, yy + 1, iz, 'brick', 'column', 0xb49278);
    b.h = Math.max(b.h, yy + 2);
  }
  if (!pitched && w >= 8 && d >= 8) {
    for (let ix = 3; ix < Math.min(w - 2, 5); ix++)
      for (let iz = 3; iz < Math.min(d - 2, 5); iz++) {
        if (!filled.has(key(ix, roofY, iz))) continue;
        put(ix, roofY + 1, iz, 'metal', 'prop', 0x8c9e9b);
        b.h = Math.max(b.h, roofY + 2);
      }
  }
  if (kind === 'chapel') {
    const tower = roofY + 7;
    for (let y = 1; y <= tower; y++)
      for (let zz = 0; zz < 4; zz++)
        for (let xx = 0; xx < 4; xx++) {
          filled.delete(key(xx, y, zz));
          const edge = xx === 0 || xx === 3 || zz === 0 || zz === 3;
          const pier = (xx === 0 || xx === 3) && (zz === 0 || zz === 3);
          if (y === tower || y === tower - 3 || (edge && (y < tower - 3 || pier)))
            put(
              xx,
              y,
              zz,
              'brick',
              y === tower ? 'roof' : pier ? 'column' : 'wall',
              y === tower ? 0xb2b6a6 : color,
            );
        }
    for (const [xx, zz] of [
      [1, 1],
      [1, 2],
      [2, 1],
      [2, 2],
    ])
      put(xx, tower + 1, zz, 'metal', 'roof', 0x7b9291);
    put(1, tower + 2, 1, 'metal', 'roof', 0x657c7c);
    for (let xx = 6; xx < Math.min(w - 2, 8); xx++) for (let y = 1; y < 4; y++) filled.delete(key(xx, y, 0));
    b.h = Math.max(b.h, tower + 3);
  }
  b.cells = [...filled.values()];
  return b;
}

/** Constructed scenery uses the exact same damageable cells as buildings. */
export function createProp(id: number, x: number, z: number, kind: PropKind, variant = 0): Building {
  const b: Building = {
    id,
    x,
    z,
    kind,
    w: 1,
    d: 1,
    h: 1,
    color: 0xb8bbae,
    accent: 0x668277,
    cells: [],
    category: kind === 'bridge' ? 'bridge' : 'prop',
    name: `${kind} ${id + 1}`,
  };
  const filled = new Map<number, Cell>();
  const put = (ix: number, y: number, iz: number, material: Material, role: CellRole, color: number) => {
    const k = key(ix, y, iz);
    filled.set(k, {
      id: id * 1000000 + k,
      x: ix,
      y,
      z: iz,
      hp: MATERIALS[material].strength,
      material,
      role,
      color,
    });
    b.w = Math.max(b.w, ix + 1);
    b.d = Math.max(b.d, iz + 1);
    b.h = Math.max(b.h, y + 1);
  };
  if (kind === 'tree') {
    const height = 3 + (variant % 3);
    for (let y = 0; y <= height; y++) put(1, y, 1, 'wood', 'column', 0x8e7653);
    for (let y = height - 1; y <= height + 1; y++)
      for (let iz = 0; iz < 3; iz++)
        for (let ix = 0; ix < 3; ix++)
          if (!(y === height + 1 && (ix !== 1 || iz !== 1)) && !(ix === 1 && iz === 1 && y <= height))
            put(ix, y, iz, 'foliage', 'prop', [0x6e9461, 0x82a76d, 0x5e896f][variant % 3]);
  } else if (kind === 'bench') {
    for (let ix = 0; ix < 2; ix++) put(ix, 0, 0, 'wood', 'prop', 0xad815b);
  } else if (kind === 'lamp') {
    for (let y = 0; y < 4; y++) put(0, y, 0, 'metal', 'column', 0x637f80);
    put(1, 3, 0, 'metal', 'beam', 0xbebf9b);
  } else if (kind === 'fence') {
    for (let ix = 0; ix < 7; ix++) put(ix, 0, 0, 'wood', 'prop', variant % 2 ? 0xb7b4a2 : 0x927c5b);
  } else if (kind === 'planter') {
    for (let ix = 0; ix < 2; ix++) {
      put(ix, 0, 0, 'concrete', 'prop', 0xc5c8b2);
      put(ix, 1, 0, 'foliage', 'prop', 0x90a76b);
    }
  } else if (kind === 'shelter') {
    for (let y = 0; y < 3; y++)
      for (const ix of [0, 4]) for (const iz of [0, 3]) put(ix, y, iz, 'wood', 'column', 0xb08b63);
    for (let ix = 0; ix < 5; ix++) for (let iz = 0; iz < 4; iz++) put(ix, 3, iz, 'wood', 'roof', 0x638878);
  } else if (kind === 'fountain') {
    for (let ix = 0; ix < 5; ix++)
      for (let iz = 0; iz < 5; iz++) put(ix, 0, iz, 'concrete', 'slab', 0xbecabf);
    for (let y = 1; y <= 2; y++) put(2, y, 2, 'concrete', 'column', 0xd9dccc);
    for (let ix = 1; ix <= 3; ix++) put(ix, 3, 2, 'concrete', 'slab', 0xd9dccc);
  } else if (kind === 'paving') {
    for (let ix = 0; ix < 7; ix++)
      for (let iz = 0; iz < 2; iz++) put(ix, 0, iz, 'concrete', 'slab', 0xd8d4bb);
  } else if (kind === 'bridge') {
    for (const ix of [0, 13, 27])
      for (const iz of [0, 5]) for (let y = 0; y < 3; y++) put(ix, y, iz, 'concrete', 'column', 0xb4beb1);
    for (let ix = 0; ix < 28; ix++)
      for (let iz = 0; iz < 6; iz++) {
        put(ix, 3, iz, 'metal', 'beam', iz === 0 || iz === 5 ? 0xa8b8ad : 0x7a8988);
        if ((iz === 0 || iz === 5) && ix % 4 === 0) put(ix, 4, iz, 'metal', 'column', 0xcbd4c8);
        if (iz === 0 || iz === 5) put(ix, 5, iz, 'metal', 'beam', 0xcbd4c8);
      }
  }
  b.cells = [...filled.values()];
  // Crown cells include their small woody twigs: finite attachment strength, far below a trunk.
  const bounds = (c: Cell, x: number, y: number, z: number, w: number, h: number, d: number) => {
    c.bounds = { x: x / CELL, y: y / CELL, z: z / CELL, w: w / CELL, h: h / CELL, d: d / CELL };
  };
  for (const c of b.cells) {
    if (c.material === 'foliage') c.capacity = 8;
    if (kind === 'paving') bounds(c, 0.02, 0, 0.02, CELL - 0.04, 0.12, CELL - 0.04);
    else if (kind === 'bench') bounds(c, 0, 0, 0.24, CELL, 1.05, 0.55);
    else if (kind === 'fence') bounds(c, 0, 0, 0.5, CELL, 1.1, 0.19);
    else if (kind === 'lamp') {
      if (c.x === 1) bounds(c, 0, 0.54, 0.47, 0.9, 0.21, 0.34);
      else if (c.y === 0) bounds(c, 0.38, 0, 0.38, 0.48, CELL, 0.48);
      else if (c.y === 3) bounds(c, 0.53, 0, 0.51, 0.72, CELL, 0.24);
      else bounds(c, 0.53, 0, 0.53, 0.18, CELL, 0.18);
    } else if (kind === 'fountain')
      bounds(c, 0.02, 0, 0.02, CELL - 0.04, c.y === 0 ? 0.505 : CELL, CELL - 0.04);
    else if (kind === 'shelter') {
      if (c.role === 'column') bounds(c, 0.45, 0, 0.45, 0.35, CELL, 0.35);
      else bounds(c, 0, 0, 0, CELL, 0.28, CELL);
    } else if (kind === 'planter')
      bounds(c, 0.05, 0, 0.13, CELL - 0.1, c.material === 'foliage' ? 0.65 : CELL, CELL - 0.26);
    else if (kind === 'tree' && c.material === 'wood' && c.y < b.h - 3)
      bounds(c, 0.4, 0, 0.4, 0.45, CELL, 0.45);
  }
  return b;
}

export const riverX = (z: number): number => 76 + Math.sin(z / 66) * 9;

/** A finite composed region. Activation is controlled by the game, after structural validation. */
export function generateRegion(seed = 73421): City {
  const random = seeded(seed);
  const buildings: Building[] = [],
    props: Building[] = [],
    bridges: Building[] = [];
  const roads = [-190, -146, -102, -58, -14, 30, 112, 154, 196];
  const northSouth = [-192, -148, -104, -60, -16, 28, 72, 116, 160, 198];
  const districts: District[] = [
    { id: 'downtown', name: 'Harbour Central', x: -56, z: -39, radius: 92 },
    { id: 'midtown', name: 'Civic Quarter', x: -126, z: -126, radius: 80 },
    { id: 'oldtown', name: 'Garden Ward', x: -128, z: 92, radius: 85 },
    { id: 'suburbs', name: 'Willow Heights', x: 148, z: -86, radius: 100 },
    { id: 'rural', name: 'South Meadows', x: 150, z: 143, radius: 69 },
    { id: 'river', name: 'Linden River', x: 77, z: 14, radius: 185 },
  ];
  const classify = (x: number, z: number): string =>
    x > 100
      ? z > 70
        ? 'rural'
        : 'suburbs'
      : x > -112 && z < 60 && z > -120
        ? 'downtown'
        : z > 50
          ? 'oldtown'
          : 'midtown';
  const tag = (b: Building) => {
    b.district = classify(b.x, b.z);
    b.chunk = `${Math.floor((b.x + 210) / 60)},${Math.floor((b.z + 210) / 60)}`;
    return b;
  };
  const add = (x: number, z: number, kind: Archetype, w: number, d: number, floors: number) => {
    const palette = [0xddded3, 0xcaa990, 0xe8ddc7, 0xbcc9c2, 0xba9c85, 0xd0c1a6];
    const accent =
      kind === 'glass-office'
        ? [0x548895, 0x74abb0, 0x527583][buildings.length % 3]
        : [0x487d79, 0x527b89, 0xb17e55][buildings.length % 3];
    const b = tag(
      createArchetype(
        buildings.length,
        x,
        z,
        kind,
        w,
        d,
        floors,
        palette[buildings.length % palette.length],
        accent,
      ),
    );
    buildings.push(b);
    return b;
  };
  // Deliberate downtown landmarks frame the initial view, with a connected park between them.
  add(-96, -94, 'glass-office', 13, 11, 18);
  add(-49, -94, 'setback-skyscraper', 15, 13, 22);
  add(-5, -94, 'point-tower', 12, 12, 17);
  add(-96, -49, 'masonry-highrise', 12, 11, 12);
  add(-8, -49, 'glass-office', 12, 13, 15);
  add(-94, -5, 'courtyard', 22, 21, 6);
  add(-48, -5, 'modern-apartment', 20, 13, 8);
  add(-5, -5, 'civic', 22, 13, 3);
  // The centre uses large irregular massing and secondary lanes, not a copied nine-block tile.
  for (let row = 0; row < 7; row++)
    for (let col = 0; col < 5; col++) {
      const x = -182 + col * 44,
        z = -182 + row * 44;
      if (col >= 2 && row >= 2 && row <= 4) continue;
      const old = row >= 5;
      const kind: Archetype = old
        ? (col + row) % 2
          ? 'rowhouse'
          : 'courtyard'
        : (['masonry-highrise', 'modern-apartment', 'corner-shop', 'warehouse', 'civic'][
            (col + row * 2) % 5
          ] as Archetype);
      add(
        x,
        z,
        kind,
        kind === 'courtyard' ? 22 : 15,
        kind === 'courtyard' ? 21 : 12,
        old ? 3 + (row % 2) : 3 + ((col + row) % 4),
      );
      if (kind !== 'courtyard') add(x + 21, z + 2, old ? 'pitched-house' : 'corner-shop', 8, 9, old ? 2 : 3);
      if (kind !== 'courtyard' && kind !== 'warehouse')
        add(x + 2, z + 19, old ? 'semi-detached' : 'rowhouse', 20, 8, 2 + (col % 2));
    }
  // Small neighbouring buildings sit around the towers and along the eastern urban riverfront.
  for (const [x, z] of [
    [-96, -77],
    [-50, -77],
    [-6, -77],
    [-96, -31],
    [-6, -31],
    [-47, -135],
    [-4, -135],
  ])
    add(x, z, 'corner-shop', 15, 8, 2 + (buildings.length % 2));
  // Deliberately quiet lower residential streets across the river: paired houses and gardens.
  for (const z of [-180, -136, -92, -48, -4, 40])
    for (const x of [120, 164]) {
      add(
        x,
        z,
        buildings.length % 3 === 0 ? 'semi-detached' : 'pitched-house',
        10,
        10,
        random() < 0.25 ? 1 : 2,
      );
      add(x, z + 15.5, 'pitched-house', 9, 9, random() < 0.42 ? 1 : 2);
    }
  add(120, 87, 'barn', 15, 11, 1);
  add(166, 132, 'barn', 18, 12, 1);
  add(166, 89, 'pitched-house', 10, 9, 2);
  add(-181, 124, 'warehouse', 25, 18, 2);
  add(-136, 124, 'civic', 23, 17, 2);
  add(-91, 124, 'rowhouse', 23, 11, 3);
  add(-47, 124, 'semi-detached', 19, 12, 2);
  add(-5, 124, 'courtyard', 22, 21, 4);
  let nextId = buildings.length;
  const prop = (x: number, z: number, kind: PropKind, v = 0) => {
    const b = tag(createProp(nextId++, x, z, kind, v));
    props.push(b);
    return b;
  };
  // Central garden: every constructed feature and tree is a normal structural object.
  prop(-49, -49, 'fountain');
  prop(-36, -47, 'shelter');
  for (const x of [-53, -31]) for (const z of [-54, -40, -27]) prop(x, z, 'tree', props.length);
  for (const z of [-54, -29]) {
    prop(-46, z, 'bench');
    prop(-39, z, 'bench');
    prop(-51, z, 'lamp');
  }
  for (const z of [-42, -36]) {
    prop(-55, z, 'paving');
    prop(-38, z, 'paving');
  }
  for (const x of [-55, -45, -35]) {
    prop(x, -56, 'fence');
    prop(x, -21, 'planter');
  }
  // Boulevard trees and lights, kept away from carriageways and building footprints.
  for (const z of [-171, -126, -82, -38, 6, 50, 94, 138])
    for (const x of [-110, 20]) {
      prop(x, z, 'tree', props.length);
      prop(x + 1.3, z + 8, 'lamp');
    }
  for (const z of [-173, -129, -85, -41, 3, 47])
    for (const x of [138, 182]) {
      prop(x, z, 'tree', props.length);
      prop(x - 1, z + 9, 'fence');
    }
  // Rural hedges and farm fences leave wide grazing enclosures for wildlife.
  for (const z of [111, 155, 183]) for (const x of [118, 128, 138, 162, 172, 182]) prop(x, z, 'fence', 1);
  for (const z of [-181, -120, -61, 0, 60, 121, 181]) {
    prop(riverX(z) - 17, z, 'tree', props.length);
    prop(riverX(z) + 14, z, 'tree', props.length);
  }
  const nodes: RoadNode[] = [],
    edges: RoadEdge[] = [];
  const nodeMap = new Map<string, number>();
  const node = (x: number, z: number) => {
    const k = `${x},${z}`;
    const found = nodeMap.get(k);
    if (found !== undefined) return found;
    const id = nodes.length;
    nodes.push({ id, x, z });
    nodeMap.set(k, id);
    return id;
  };
  const edge = (a: number, b: number, width: number, kind: string, bridgeId?: number) =>
    edges.push({
      id: edges.length,
      from: a,
      to: b,
      width,
      kind,
      ...(bridgeId !== undefined ? { bridgeId } : {}),
    });
  const bridgeRows = [-104, -16, 116];
  for (const x of roads)
    for (let i = 0; i < northSouth.length - 1; i++)
      edge(
        node(x, northSouth[i]),
        node(x, northSouth[i + 1]),
        x === -14 || x === -102 ? 9.5 : x > 100 ? 6 : 7,
        x === -14 || x === -102 ? 'avenue' : x > 100 ? 'residential' : 'street',
      );
  for (const z of northSouth)
    for (let i = 0; i < roads.length - 1; i++) {
      const x1 = roads[i],
        x2 = roads[i + 1];
      if (x1 === 30 && x2 === 112) {
        if (!bridgeRows.includes(z)) continue;
        const bridge = tag(createProp(nextId++, riverX(z) - 17.5, z - 3.75, 'bridge'));
        bridges.push(bridge);
        edge(node(x1, z), node(x2, z), 7.5, 'bridge', bridge.id);
      } else
        edge(
          node(x1, z),
          node(x2, z),
          bridgeRows.includes(z) ? 9.5 : z > 70 && x1 > 100 ? 5.5 : 7,
          bridgeRows.includes(z) ? 'avenue' : 'street',
        );
    }
  // Preserve deterministic state even if future rendering chooses a different chunk order.
  return {
    seed,
    buildings,
    props,
    bridges,
    roads,
    half: 210,
    chunkSize: 60,
    initialTarget: { x: -55, z: -40 },
    districts,
    river: {
      width: 21,
      points: Array.from({ length: 29 }, (_, i) => ({ x: riverX(-210 + i * 15), z: -210 + i * 15 })),
    },
    roadGraph: { nodes, edges },
  };
}
